import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiDollarSign,
  FiFilePlus,
  FiPercent,
  FiRefreshCw,
  FiSend,
  FiTrendingUp,
} from "react-icons/fi";

import "./Fechamento.css";

import {
  adicionarNovoAtivoOffline,
  calcularTotalDeCotasDoDia,
  calcularTotalDoFechamentoDoDiaUSD,
  handleConfirm,
  handleConfirmPrivate,
  obterCotacoes,
  obterCotacoesPrivate,
  obterFundoFinaceiroPrivate,
  obterIdFundoFinanceiroAssessoria,
  obterValorReal,
  removerNovoAtivoOffline,
} from "./functions";

import Footer from "../../../components/Footer/Footer";
import { Loading } from "../../../components/Loading";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import { ModalNovoAtivoFechamento } from "../../../components/modais/ModalNovoAtivoFechamento";
import Navbar from "../../../components/Navbar/Navbar";
import { TabelaFechamento } from "../../../components/Tabelas/TabelaFechamento";
import { mascaraBRL, mascaraUSD, transformarValorParaBRLEmInput } from "../../../functions/MascarasMonetarias";
import { Erro } from "../../../pages/Cliente/AlteracaoSenha/AlteracaoSenha";

export interface Cotacoes {
  nome: string;
  porcentagemAtivo?: number;
  quantidadeAtivo: number;
  sigla: string;
  valorTotalAtivo: number;
  valorUnitarioAtivo: number;
  adicionadoManualmente?: boolean;
  origem?: string;
}

const SERVICO_ASSESSORIA = 1;
const SERVICO_PRIVATE = 3;

const formatPercent = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const Fechamento = () => {
  const [modalNovoAtivoFechamento, setModalNovoAtivoFechamento] = useState<boolean>(false);
  const [carregando, setCarregando] = useState(false);
  const [cotacoes, setCotacoes] = useState<Array<Cotacoes>>([]);
  const [valorTotal, setValorTotal] = useState<number>(0);
  const [totalUsd, setTotalUsd] = useState(0);
  const [totalBRL, setTotalBRL] = useState(0);
  const [valorReal, setValorReal] = useState<number>(0);
  const [fundoFinaceiroId, setFundoFinaceiroId] = useState<string>("");
  const [servicoFinaceiroId, setServicoFinaceiroId] = useState<number>(SERVICO_ASSESSORIA);
  const [sucesso, setSucesso] = useState<boolean>(false);
  const [erro, setErro] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [desconectar, setDesconectar] = useState<boolean>(false);
  const [totalCotaDia, setTotalCotaDia] = useState<number>(0);
  const [valorCotaDia, setValorCotaDia] = useState<string>("");
  const [taxaPrivate, setTaxaPrivate] = useState<number>(0);
  const [cobrarTaxa, setCobrarTaxa] = useState<boolean>(false);
  const [taxaAssessoria, setTaxaAssessoria] = useState<number>(0);
  const [, setCotacaoErro] = useState<Erro>({ erro: false, mensagem: "" });

  const { idCredenciais } = useParams();
  const navigate = useNavigate();

  const clientePrivate = Boolean(idCredenciais);
  const servicoLabel = clientePrivate ? "Private" : "Assessoria";
  const taxaSelecionada = clientePrivate ? taxaPrivate : taxaAssessoria;

  useEffect(() => {
    const carregarFechamento = async () => {
      setErro(false);
      setSucesso(false);
      setCarregando(true);

      await obterValorReal(setValorReal, setCotacaoErro);

      if (idCredenciais) {
        setServicoFinaceiroId(SERVICO_PRIVATE);
        await obterCotacoesPrivate(
          idCredenciais,
          setCotacoes,
          setValorTotal,
          setCarregando,
          setDesconectar
        );
        await obterFundoFinaceiroPrivate(
          idCredenciais,
          setFundoFinaceiroId,
          setErro,
          setDesconectar
        );
        return;
      }

      setServicoFinaceiroId(SERVICO_ASSESSORIA);
      await obterCotacoes(setCotacoes, setValorTotal, setCarregando, setDesconectar);
      await obterIdFundoFinanceiroAssessoria(
        setFundoFinaceiroId,
        setErro,
        setDesconectar
      );
    };

    carregarFechamento();
  }, [idCredenciais, reload]);

  useEffect(() => {
    setTotalBRL(totalUsd * Number(valorReal || 0));
  }, [totalUsd, valorReal]);

  useEffect(() => {
    calcularTotalDoFechamentoDoDiaUSD(cotacoes, setTotalUsd);
  }, [cotacoes]);

  useEffect(() => {
    if (!clientePrivate && totalBRL > 0) {
      calcularTotalDeCotasDoDia(
        servicoFinaceiroId,
        totalBRL,
        setTotalCotaDia,
        setErro
      );
    }
  }, [clientePrivate, servicoFinaceiroId, totalBRL]);

  const maiorPosicao = useMemo(
    () =>
      cotacoes.reduce<Cotacoes | undefined>((maior, cotacao) => {
        if (!maior || cotacao.valorTotalAtivo > maior.valorTotalAtivo) {
          return cotacao;
        }

        return maior;
      }, undefined),
    [cotacoes]
  );

  const posicaoMaiorPercentual = maiorPosicao && valorTotal
    ? (maiorPosicao.valorTotalAtivo * 100) / valorTotal
    : 0;

  const ativosManuais = cotacoes.filter((cotacao) => cotacao.adicionadoManualmente).length;

  const recarregar = () => setReload((valorAtual) => !valorAtual);

  const confirmarFechamento = () => {
    if (clientePrivate) {
      handleConfirmPrivate(
        taxaPrivate,
        cobrarTaxa,
        cotacoes,
        fundoFinaceiroId,
        servicoFinaceiroId,
        totalBRL,
        totalUsd,
        setErro,
        setSucesso,
        setDesconectar,
        setCarregando,
        valorTotal
      );
      return;
    }

    handleConfirm(
      taxaAssessoria,
      cobrarTaxa,
      cotacoes,
      fundoFinaceiroId,
      servicoFinaceiroId,
      totalBRL,
      totalUsd,
      totalCotaDia,
      setTotalCotaDia,
      setErro,
      setSucesso,
      setDesconectar,
      setCarregando
    );
  };

  const handleValorCotaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormatado = transformarValorParaBRLEmInput(event.target.value, setValorCotaDia);

    setValorCotaDia(valorFormatado);
    setTotalCotaDia(Number(valorFormatado.replace(/\./g, "").replace(/,/g, ".")));
  };

  const handleTaxaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valor = Number(event.target.value);

    if (clientePrivate) {
      setTaxaPrivate(valor);
      return;
    }

    setTaxaAssessoria(valor);
  };

  return (
    <>
      {carregando && <Loading />}
      <Navbar />

      <main className="fechamento-page">
        <section className="fechamento-hero">
          <div>
            <span>Gestão financeira</span>
            <h1>Fechamento</h1>
            <p>
              Revise posições, ajuste ativos manuais e envie o fechamento do dia
              com totais em dólar e real para a operação Omex Capital.
            </p>
          </div>

          <div className="fechamento-hero-actions">
            <strong>{servicoLabel}</strong>
            <button type="button" onClick={recarregar} disabled={carregando}>
              <FiRefreshCw />
              Atualizar dados
            </button>
          </div>
        </section>

        <section className="fechamento-summary" aria-label="Resumo do fechamento">
          <article>
            <FiDollarSign />
            <span>Total USD</span>
            <strong>US$ {mascaraUSD(totalUsd.toFixed(2))}</strong>
          </article>
          <article>
            <FiTrendingUp />
            <span>Total BRL</span>
            <strong>R$ {mascaraBRL(totalBRL.toFixed(2))}</strong>
          </article>
          <article>
            <FiFilePlus />
            <span>Ativos</span>
            <strong>{cotacoes.length}</strong>
            <small>{ativosManuais} manual(is)</small>
          </article>
          <article>
            <FiPercent />
            <span>Maior posição</span>
            <strong>{maiorPosicao?.sigla || "-"}</strong>
            <small>{formatPercent(posicaoMaiorPercentual)}%</small>
          </article>
        </section>

        <section className="fechamento-panel">
          <div className="fechamento-panel-header">
            <div>
              <span>Carteira</span>
              <h2>Ativos do fechamento</h2>
            </div>
            <button
              type="button"
              className="fechamento-secondary-action"
              onClick={() => setModalNovoAtivoFechamento(true)}
            >
              <FiFilePlus />
              Adicionar ativo
            </button>
          </div>

          {erro ? (
            <div className="fechamento-state error">
              <FiAlertCircle />
              <strong>Não foi possível carregar ou enviar o fechamento.</strong>
              <button type="button" onClick={recarregar}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : sucesso ? (
            <div className="fechamento-state success">
              <FiCheckCircle />
              <strong>Fechamento enviado com sucesso.</strong>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    clientePrivate
                      ? "/funcionario/clientes-private"
                      : "/gerenciar/historico-fechamento"
                  )
                }
              >
                Ver histórico
              </button>
            </div>
          ) : (
            <>
              <div className="fechamento-table-wrap">
                <table id="tblFechamento" className="tabelaFechamento">
                  <thead>
                    <tr className="tabelaFechamentoHeader">
                      <th className="tableTituloFechamento">Ativo</th>
                      <th className="tableTituloFechamento colunaQuantidade">Quantidade</th>
                      <th className="tableTituloFechamento">Valor unitário</th>
                      <th className="tableTituloFechamento colunaPorcentagemAtivo">
                        % na carteira
                      </th>
                      <th className="tableTituloFechamento">Total</th>
                    </tr>
                  </thead>
                  <tbody id="tbodyPrincipal">
                    {cotacoes.length ? (
                      cotacoes.map((cotacao, index) => (
                        <TabelaFechamento
                          key={`${cotacao.nome}-${cotacao.valorTotalAtivo}-${index}`}
                          ativo={cotacao.nome}
                          quantidade={cotacao.quantidadeAtivo}
                          valorUnitario={cotacao.valorUnitarioAtivo}
                          valorTotal={valorTotal}
                          total={cotacao.valorTotalAtivo}
                          manual={cotacao.adicionadoManualmente}
                          removerNovoAtivoOffline={() =>
                            removerNovoAtivoOffline(
                              index,
                              cotacoes,
                              setCotacoes,
                              setValorTotal,
                              valorTotal
                            )
                          }
                          index={index}
                          listaCotacoes={cotacoes}
                          setListaCotacoes={setCotacoes}
                          sigla={cotacao.sigla}
                        />
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>Nenhum ativo encontrado para fechamento.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="fechamento-controls">
                {!clientePrivate && (
                  <label>
                    <span>Valor da cota do dia</span>
                    <input
                      maxLength={13}
                      type="text"
                      id="totalCotaDia"
                      autoComplete="off"
                      name="totalCotaDia"
                      value={valorCotaDia ? `R$ ${valorCotaDia}` : ""}
                      onChange={handleValorCotaChange}
                      placeholder="R$ 0,00"
                    />
                    <small>
                      Estimado: R$ {mascaraBRL(totalCotaDia.toFixed(2))}
                    </small>
                  </label>
                )}

                <label className="fechamento-toggle">
                  <input
                    type="checkbox"
                    id="chkCobrarTaxas"
                    name="chkCobrarTaxas"
                    checked={cobrarTaxa}
                    onChange={(event) => setCobrarTaxa(event.target.checked)}
                  />
                  <span>Cobrar taxas neste fechamento</span>
                </label>

                {cobrarTaxa && (
                  <label>
                    <span>Taxa cobrada</span>
                    <div className="fechamento-percent-input">
                      <input
                        maxLength={3}
                        type="number"
                        min={0}
                        id="taxaCobrada"
                        autoComplete="off"
                        name="taxaCobrada"
                        value={taxaSelecionada}
                        onChange={handleTaxaChange}
                      />
                      <strong>%</strong>
                    </div>
                  </label>
                )}
              </div>

              <div className="fechamento-submit-row">
                <button
                  type="button"
                  className="fechamento-primary-action"
                  onClick={confirmarFechamento}
                  disabled={carregando || !cotacoes.length}
                >
                  {carregando ? <LoadingButton /> : <FiSend />}
                  Confirmar fechamento
                </button>
              </div>
            </>
          )}
        </section>
      </main>

      <ModalNovoAtivoFechamento
        modal={modalNovoAtivoFechamento}
        setModal={() => setModalNovoAtivoFechamento(false)}
        handleConfirm={(ativo) =>
          adicionarNovoAtivoOffline(ativo, cotacoes, setCotacoes, setValorTotal, valorTotal)
        }
      />

      {desconectar && <ModalDesconectar modal={desconectar} />}
      <Footer />
    </>
  );
};

export default Fechamento;
